import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  BadgeCheck, CheckCircle2, FileCode2, Loader2, RefreshCw, Send,
  ShieldCheck,
} from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { ErrorBox, StatusPill } from "@/components/common";

type AdoptionMode = "none" | "reference" | "align" | "import_subset" | "import_full";

interface AdoptionActionRecord {
  id: string;
  mode: AdoptionMode;
  status: "DRAFT" | "ACTIVE" | "BLOCKED" | "EXPIRED" | "REJECTED";
  source_version: string;
  source_hash: string;
  intended_use: string;
  selected_modules: string[];
  adoption_owner: string;
  customer_rights_evidence: string;
  rights_reviewed_by: string;
  rights_reviewed_at: string | null;
  semantic_reviewed_by: string;
  semantic_reviewed_at: string | null;
  technical_compatibility_confirmed: boolean;
  acceptance_test: string;
  mapping_ttl: string;
  review_due_at: string | null;
  promotion_status: "NONE" | "REQUESTED" | "STAFF_REVIEW" | "PUBLISHED" | "REJECTED";
}

interface ReviewPayload {
  review_kind: "rights" | "semantic" | "technical";
  [key: string]: unknown;
}

function futureIso(date: string): string {
  return new Date(`${date}T23:59:59`).toISOString();
}

function initialDate(value: string | null, days = 365): string {
  const date = value ? new Date(value) : new Date(Date.now() + days * 86400000);
  return date.toISOString().slice(0, 10);
}

export function StandardAdoptionActions({ adoption, sourceSlug, onChanged }: {
  adoption: AdoptionActionRecord;
  sourceSlug: string;
  onChanged: () => void;
}) {
  const [sourceVersion, setSourceVersion] = useState(adoption.source_version);
  const [sourceHash, setSourceHash] = useState(adoption.source_hash);
  const [intendedUse, setIntendedUse] = useState(adoption.intended_use);
  const [owner, setOwner] = useState(adoption.adoption_owner);
  const [rightsEvidence, setRightsEvidence] = useState(adoption.customer_rights_evidence);
  const [reviewDue, setReviewDue] = useState(initialDate(adoption.review_due_at));
  const [mappingTtl, setMappingTtl] = useState(adoption.mapping_ttl);
  const [modules, setModules] = useState(adoption.selected_modules.join(", "));
  const [acceptanceTest, setAcceptanceTest] = useState(adoption.acceptance_test);
  const [renewalDate, setRenewalDate] = useState(initialDate(adoption.review_due_at));
  const [renewalRationale, setRenewalRationale] = useState("");
  const [semver, setSemver] = useState("0.1.0");
  const [promotionEvidence, setPromotionEvidence] = useState("");
  const [confirmations, setConfirmations] = useState({
    provenance_confirmed: false,
    mapping_derivative_rights_confirmed: false,
    customer_export_rights_confirmed: false,
    redistribution_rights_confirmed: false,
  });

  const review = useMutation({
    mutationFn: (payload: ReviewPayload) => rawFetch(
      `/composition/standard-adoptions/${adoption.id}/review/`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
    onSuccess: onChanged,
  });
  const renew = useMutation({
    mutationFn: () => rawFetch(
      `/composition/standard-adoptions/${adoption.id}/renew/`,
      {
        method: "POST",
        body: JSON.stringify({
          review_due_at: futureIso(renewalDate),
          rationale: renewalRationale,
        }),
      },
    ),
    onSuccess: onChanged,
  });
  const promote = useMutation({
    mutationFn: () => rawFetch(
      `/composition/standard-adoptions/${adoption.id}/promote-to-reusable/`,
      {
        method: "POST",
        body: JSON.stringify({ semver, rights_evidence: promotionEvidence, ...confirmations }),
      },
    ),
    onSuccess: onChanged,
  });
  const actionError = review.error ?? renew.error ?? promote.error;
  const fiboSubset = sourceSlug === "fibo" && adoption.mode !== "none" && adoption.mode !== "reference";
  const importsSource = fiboSubset || adoption.mode === "import_subset" || adoption.mode === "import_full";

  return (
    <div className="space-y-4">
      <section className="border-y border-border py-3">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold"><ShieldCheck size={14} /> Rights review</div>
          {adoption.rights_reviewed_by && <StatusPill status="approved" />}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Exact source version" value={sourceVersion} onChange={setSourceVersion} />
          <Field label="Source artifact hash" value={sourceHash} onChange={setSourceHash} />
          <Field label="Adoption owner" value={owner} onChange={setOwner} />
          <label className="text-[11px] text-muted">Review due
            <input className="input mt-1 w-full" type="date" value={reviewDue} onChange={(event) => setReviewDue(event.target.value)} />
          </label>
        </div>
        <TextField label="Intended use" value={intendedUse} onChange={setIntendedUse} />
        <TextField label="Controlled evidence reference" value={rightsEvidence} onChange={setRightsEvidence} />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <ActionButton pending={review.isPending} icon={<BadgeCheck size={13} />} onClick={() => review.mutate({
            review_kind: "rights", source_version: sourceVersion, source_hash: sourceHash,
            intended_use: intendedUse, adoption_owner: owner,
            customer_rights_evidence: rightsEvidence, review_due_at: futureIso(reviewDue),
          })}>Record rights review</ActionButton>
          {adoption.rights_reviewed_by && <AuditStamp actor={adoption.rights_reviewed_by} at={adoption.rights_reviewed_at} />}
        </div>
      </section>

      {adoption.mode !== "none" && adoption.mode !== "reference" && (
        <section className="border-b border-border pb-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold"><FileCode2 size={14} /> Semantic review</div>
            {adoption.semantic_reviewed_by && <StatusPill status="approved" />}
          </div>
          {adoption.mode === "align" && (
            <label className="block text-[11px] text-muted">Thin-layer mapping (Turtle)
              <textarea className="input mt-1 min-h-40 w-full font-mono text-[11px]" spellCheck={false} value={mappingTtl} onChange={(event) => setMappingTtl(event.target.value)} />
            </label>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ActionButton pending={review.isPending} icon={<CheckCircle2 size={13} />} onClick={() => review.mutate({
              review_kind: "semantic", mapping_ttl: adoption.mode === "align" ? mappingTtl : "",
            })}>Record semantic review</ActionButton>
            {adoption.semantic_reviewed_by && <AuditStamp actor={adoption.semantic_reviewed_by} at={adoption.semantic_reviewed_at} />}
          </div>
        </section>
      )}

      {importsSource && (
        <section className="border-b border-border pb-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-xs font-semibold">Technical and acceptance review</div>
            {adoption.technical_compatibility_confirmed && <StatusPill status="approved" />}
          </div>
          {fiboSubset ? (
            <fieldset className="my-2 flex flex-wrap gap-3 text-[11px]">
              <legend className="mb-1 text-muted">Pinned FIBO subset (BE, FND, FBC only)</legend>
              {(["BE", "FND", "FBC"] as const).map((module) => (
                <label key={module} className="flex items-center gap-1">
                  <input type="checkbox" checked={modules.split(",").map((item) => item.trim()).includes(module)} onChange={(event) => {
                    const next = new Set(modules.split(",").map((item) => item.trim()).filter(Boolean));
                    if (event.target.checked) next.add(module); else next.delete(module);
                    setModules([...next].join(", "));
                  }} /> {module}
                </label>
              ))}
            </fieldset>
          ) : adoption.mode === "import_subset" && <Field label="Official modules (comma separated)" value={modules} onChange={setModules} />}
          <TextField label="Acceptance test" value={acceptanceTest} onChange={setAcceptanceTest} />
          <ActionButton pending={review.isPending} icon={<CheckCircle2 size={13} />} onClick={() => review.mutate({
            review_kind: "technical",
            selected_modules: modules.split(",").map((item) => item.trim()).filter(Boolean),
            acceptance_test: acceptanceTest,
          })}>Record technical review</ActionButton>
        </section>
      )}

      {(adoption.status === "EXPIRED" || adoption.review_due_at) && (
        <section className="border-b border-border pb-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold"><RefreshCw size={13} /> Renewal</div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-[11px] text-muted">New review due date
              <input className="input mt-1 w-full" type="date" value={renewalDate} onChange={(event) => setRenewalDate(event.target.value)} />
            </label>
            <Field label="Renewal rationale" value={renewalRationale} onChange={setRenewalRationale} />
          </div>
          <ActionButton pending={renew.isPending} icon={<RefreshCw size={13} />} disabled={!renewalRationale.trim()} onClick={() => renew.mutate()}>Renew and re-evaluate</ActionButton>
        </section>
      )}

      {adoption.mode === "align" && adoption.mapping_ttl.trim() && (
        <section>
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold"><Send size={13} /> Reusable promotion</div>
            {adoption.promotion_status !== "NONE" && <StatusPill status={adoption.promotion_status.toLowerCase()} />}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Draft pack semver" value={semver} onChange={setSemver} />
            <Field label="Promotion evidence reference" value={promotionEvidence} onChange={setPromotionEvidence} />
          </div>
          <div className="my-2 grid gap-1 text-[11px] sm:grid-cols-2">
            {Object.entries(confirmations).map(([key, checked]) => (
              <label key={key} className="flex items-start gap-2">
                <input type="checkbox" checked={checked} onChange={(event) => setConfirmations((current) => ({ ...current, [key]: event.target.checked }))} />
                {key.replaceAll("_", " ")}
              </label>
            ))}
          </div>
          <ActionButton pending={promote.isPending} icon={<Send size={13} />} disabled={adoption.status !== "ACTIVE" || !promotionEvidence.trim() || !Object.values(confirmations).every(Boolean)} onClick={() => promote.mutate()}>Submit signed Tier 1 draft</ActionButton>
        </section>
      )}

      {actionError && <ErrorBox error={actionError} />}
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="block text-[11px] text-muted">{label}<input className="input mt-1 w-full" value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="mt-2 block text-[11px] text-muted">{label}<textarea className="input mt-1 min-h-16 w-full" value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function ActionButton({ pending, icon, children, onClick, disabled = false }: {
  pending: boolean; icon: ReactNode; children: ReactNode; onClick: () => void; disabled?: boolean;
}) {
  return <button type="button" className="btn" disabled={pending || disabled} onClick={onClick}>{pending ? <Loader2 size={13} className="mr-1 inline animate-spin" /> : <span className="mr-1 inline-flex align-middle">{icon}</span>}{children}</button>;
}

function AuditStamp({ actor, at }: { actor: string; at: string | null }) {
  return <span className="text-[11px] text-muted">{actor}{at ? ` · ${new Date(at).toLocaleString()}` : ""}</span>;
}
